import type { SpecToolDecision } from '@parcel/types';
import { Button, Tooltip, TooltipContent, TooltipTrigger, cn } from '@parcel/ui';
import { Ban, Building2, CircleCheck, Hand, Loader2, ShieldCheck, UserRound } from 'lucide-react';
import * as React from 'react';
import { DECISION_LABELS, DECISION_STRICTNESS } from '../../lib/spec-tools.js';

/**
 * Which policy layer a control edits. `workspace` is the owner/admin default
 * for the whole workspace; `member` is one member's personal override on top
 * of it. The same component renders both.
 */
export type SpecToolPolicyScope = 'workspace' | 'member';

/** The subset of a tool row this control actually reads; `SpecToolSettingsRow` (native tools)
    and a connection's own tool policy row both satisfy it structurally. */
export interface SpecToolPolicyControlRow {
  name: string;
  title: string;
  registryFloor: SpecToolDecision;
  workspaceDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
  memberCanAlwaysAllow?: boolean;
  operationSpecificPolicy?: boolean;
}

export interface SpecToolPolicyControlProps {
  /** The tool row this control edits one decision layer of. */
  row: SpecToolPolicyControlRow;
  scope: SpecToolPolicyScope;
  /** The decision to show as selected. */
  value: SpecToolDecision | null;
  /** True when the viewer may not edit this layer at all. */
  disabled: boolean;
  saving?: boolean;
  error?: string | null;
  /**
   * True when the viewer is an owner/admin, whose personal choice replaces the
   * workspace policy rather than stacking under it. Only affects `member`
   * scope: it is what lets an admin loosen their own posture back down to the
   * registry floor.
   */
  outranksWorkspace?: boolean;
  /**
   * Names the layer beneath the member's own, for the sticker on an inherited choice.
   *
   * The settings list sits on the workspace's policy, but a connector's does not: a personal
   * connection has no workspace layer at all, so "Set by your workspace" named something that
   * does not exist. Callers on a different stack say what theirs is.
   */
  inheritedLayerLabel?: string;
  /**
   * Clears this layer's override. Omit it and no Reset renders -- absence is how a caller
   * says "nothing to reset here", which is also what marks the row as un-overridden.
   *
   * It lives inside this control rather than beside it so it sits flush against the chips it
   * resets: as a sibling it was separated from them by the saving slot's reserved width.
   */
  onReset?: () => void;
  /** Accessible name for Reset: callers name the layer it falls back to. */
  resetLabel?: string;
  onChange: (decision: SpecToolDecision | null) => void;
  className?: string;
}

const CHOICES: readonly SpecToolDecision[] = ['always_allow', 'needs_approval', 'blocked'];

/**
 * One icon per decision, matching the vocabulary the other AI tool-permission
 * surfaces have settled on: a check to run it, a raised hand to stop and ask,
 * a strike to refuse. The written label is never dropped, only moved into the
 * accessible name, so the control stays operable by screen reader and findable
 * by `getByRole('radio', { name: 'Always allow' })`.
 */
const DECISION_ICONS: Readonly<Record<SpecToolDecision, typeof CircleCheck>> = {
  always_allow: CircleCheck,
  needs_approval: Hand,
  blocked: Ban,
};

/**
 * How long a write may run before the row admits it is still working.
 *
 * A single tool policy write is one small round trip and normally lands well
 * inside this, where any indicator at all is worse than none: a spinner that
 * appears and vanishes within a frame or two is just a flicker next to the
 * chip the member is looking at. Past this, silence starts reading as a dropped
 * click, so the spinner earns its place.
 */
const SAVING_INDICATOR_DELAY_MS = 600;

/**
 * `active`, but only once it has been continuously true for `delayMs`.
 *
 * Resets the moment `active` goes false, so a fast save never leaves a spinner
 * behind and a slow one never re-arms its delay mid-flight.
 */
function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = React.useState(false);
  React.useEffect(() => {
    if (!active) {
      setElapsed(false);
      return;
    }
    const timer = setTimeout(() => setElapsed(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
  return active && elapsed;
}

/**
 * The strictest decision this scope may ever select.
 *
 * Workspace scope answers only to the tool's registry floor. Member scope
 * answers to the stricter of the registry floor and the workspace's own
 * effective posture (its stored decision, or the floor itself when the
 * workspace has not set one) -- a member may only tighten what the workspace
 * already allows, never loosen past it.
 */
export function applicableSpecToolFloor(
  row: Pick<
    SpecToolPolicyControlRow,
    'registryFloor' | 'workspaceDecision' | 'memberCanAlwaysAllow'
  >,
  scope: SpecToolPolicyScope,
  /**
   * True for an owner/admin, whose personal choice replaces the workspace
   * policy instead of stacking under it. They set that policy, so it is not a
   * ceiling on them: the registry floor is. Mirrors `workspaceCeiling` in
   * apps/api/src/lib/spec/policy.ts, which is what actually enforces this --
   * the server rejects a write this control would not have offered.
   */
  outranksWorkspace = false,
): SpecToolDecision {
  if (scope === 'member' && row.memberCanAlwaysAllow === true) return 'always_allow';
  if (scope === 'workspace' || outranksWorkspace) return row.registryFloor;
  const workspacePosture = row.workspaceDecision ?? row.registryFloor;
  return DECISION_STRICTNESS[row.registryFloor] >= DECISION_STRICTNESS[workspacePosture]
    ? row.registryFloor
    : workspacePosture;
}

/**
 * Names the floor for the choices it locks out.
 *
 * Member scope must name whichever of the registry floor or the workspace's
 * own posture is actually binding, not default to blaming the workspace:
 * `applicableSpecToolFloor` falls back to the registry floor itself when
 * `workspaceDecision` is unset (or no stricter than the registry floor), and
 * in that case the workspace has not "required" anything -- the tool's own
 * built-in safety floor is the real constraint, and saying otherwise would
 * misinform the one member reading this sentence to find out why.
 *
 * Every phrasing leads with the floor rather than the tool, so no sentence ever
 * needs a possessive: titles are derived mechanically from tool names by
 * `deriveSpecToolTitle`, and `workspace_manage_views` -> "Workspace manage
 * views" would otherwise render "Workspace manage views's".
 */
export function floorLockExplanation(
  row: Pick<SpecToolPolicyControlRow, 'title' | 'registryFloor' | 'workspaceDecision'>,
  scope: SpecToolPolicyScope,
  floor: SpecToolDecision,
  outranksWorkspace = false,
): string {
  const floorLabel = DECISION_LABELS[floor];
  if (scope === 'workspace') {
    return `The registry floor for ${row.title} is "${floorLabel}": this workspace can't set it any looser.`;
  }
  const workspaceBinds =
    !outranksWorkspace &&
    row.workspaceDecision !== null &&
    DECISION_STRICTNESS[row.workspaceDecision] > DECISION_STRICTNESS[row.registryFloor];
  return workspaceBinds
    ? `${row.title} can't be set any looser than "${floorLabel}" here: that's what the workspace already requires.`
    : `The registry floor for ${row.title} is "${floorLabel}": nothing here can be set any looser.`;
}

/**
 * The three-state Always allow / Needs approval / Blocked control, rendered as
 * a compact icon group.
 *
 * Built from native same-name radios rather than a headless primitive: a
 * browser already gives grouped radios correct roving-focus arrow-key
 * behavior for free, so there is nothing to hand-roll. Choices looser than the
 * applicable floor are individually disabled and explain themselves through the
 * label's `title` and the radio's `aria-describedby`, so a locked choice never
 * reads as merely unselected.
 *
 * Pure: props in, `onChange` out. No fetching, no react-query, no server
 * state -- the page that composes this owns all of that.
 */
export function SpecToolPolicyControl({
  row,
  scope,
  value,
  disabled,
  saving = false,
  error = null,
  outranksWorkspace = false,
  inheritedLayerLabel = 'your workspace',
  onReset,
  resetLabel,
  onChange,
  className,
}: SpecToolPolicyControlProps) {
  const groupName = `spec-tool-policy-${scope}-${row.name}`;
  const floor = applicableSpecToolFloor(row, scope, outranksWorkspace);
  const showSpinner = useDelayedFlag(saving, SAVING_INDICATOR_DELAY_MS);
  const locked = DECISION_STRICTNESS[floor] > DECISION_STRICTNESS.always_allow;
  const explanation = locked ? floorLockExplanation(row, scope, floor, outranksWorkspace) : null;
  const explanationId = `${groupName}-floor-note`;
  const accessibleName =
    scope === 'workspace' ? `Workspace policy for ${row.title}` : `Your policy for ${row.title}`;

  return (
    <div
      className={cn('relative flex items-center gap-2', className)}
      data-testid="spec-tool-policy-control"
    >
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {/*
        Out of flow entirely, in the row's own empty space, so a save can neither reflow the
        row nor reserve a gap when nothing is saving. It used to be an always-present 16px
        slot, which read as a stray space wedged between Reset and the chips.
      */}
      {showSpinner ? (
        <output
          className="-translate-y-1/2 absolute top-1/2 right-full mr-1.5"
          data-testid="spec-tool-saving-spinner"
        >
          <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden />
          <span className="sr-only">{`Saving ${row.title}`}</span>
        </output>
      ) : null}
      {onReset ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={resetLabel}
          className="h-7 px-2 text-muted-foreground text-xs"
          disabled={disabled || saving}
          onClick={onReset}
        >
          Reset
        </Button>
      ) : null}
      {/*
        The track is full-strength `bg-muted`, not a tint of it. The selected
        chip is a white pill, so the track is the only thing it can read
        against: at `bg-muted/60` the two were near-identical and the selection
        was almost invisible, which is the whole job of this control.
      */}
      <fieldset
        role="radiogroup"
        className="inline-flex w-fit shrink-0 gap-0.5 rounded-lg bg-muted p-0.5"
      >
        <legend className="sr-only">{accessibleName}</legend>
        {CHOICES.map((decision) => {
          const flooredOut = DECISION_STRICTNESS[decision] < DECISION_STRICTNESS[floor];
          const isDisabled = disabled || saving || flooredOut;
          const inputId = `${groupName}-${decision}`;
          const Icon = DECISION_ICONS[decision];
          const label =
            decision === 'always_allow' && row.operationSpecificPolicy
              ? 'Always allow non-destructive actions'
              : DECISION_LABELS[decision];
          // On the personal layer, sticker the SELECTED choice with who set it:
          // the workspace building when the member is following along, their own
          // profile mark once they have overridden it.
          //
          // Only the selected one, and only here. Marking the workspace's
          // posture on an UNSELECTED chip put the sticker under the raised pill
          // of whichever choice was actually active, which is where the overlap
          // came from. And on the admin layer every row would carry the same
          // building, since the workspace is the only authority on that tab.
          //
          // The ceiling a member is tightening under is still legible without a
          // second marker competing with this one: everything looser than the
          // workspace posture is visibly disabled, and each locked choice says
          // in its own tooltip that the workspace is what requires it.
          const isSelected = value === decision;
          const showSourceSticker = scope === 'member' && isSelected;
          // An inherited choice has two possible sources and they are not the same claim:
          // a decision the layer below actually set, or nothing set anywhere and the tool
          // sitting at its own floor. Calling the second one policy overstates it.
          const inherited = row.memberDecision === null;
          const fromLayerBelow = inherited && row.workspaceDecision !== null;
          const SourceIcon = !inherited ? UserRound : fromLayerBelow ? Building2 : ShieldCheck;
          const sourceLabel = !inherited
            ? 'Your own override'
            : fromLayerBelow
              ? `Set by ${inheritedLayerLabel}`
              : "Parcel's own default for this tool";
          return (
            // Each input/label pair gets its own DOM parent, and this wrapper is
            // load-bearing: Tailwind compiles `peer-checked:x` to
            // `.peer:checked ~ .peer-checked\:x`, and `~` is the SUBSEQUENT-sibling
            // combinator, not the adjacent one. Rendered as a flat Fragment, all
            // three inputs and all three labels were siblings, so checking "Always
            // allow" styled every later chip as selected too, and one floored-out
            // choice dimmed the still-enabled ones after it. Scoping the pair keeps
            // each label reachable only from its own input.
            //
            // `contents` generates no box, so the labels stay direct flex items of
            // the fieldset and the layout is identical: selector matching walks the
            // DOM tree, which the wrapper changes, not the box tree, which it does
            // not. Radio grouping and arrow-key roving focus key off the shared
            // `name` attribute and are unaffected by the nesting.
            <div key={decision} className="contents">
              <input
                type="radio"
                id={inputId}
                name={groupName}
                value={decision}
                checked={value === decision}
                disabled={isDisabled}
                onChange={() => onChange(decision)}
                aria-describedby={flooredOut ? explanationId : undefined}
                className="peer sr-only"
              />
              {/*
                A plain <label>, not @parcel/ui's Label: that one bakes in
                `peer-disabled:opacity-70`, which would fight the `opacity-40`
                a floored-out choice wants here.
              */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <label
                    htmlFor={inputId}
                    className={cn(
                      'relative flex size-7 cursor-pointer items-center justify-center rounded-[7px] transition-colors',
                      // Unselected sits back: a muted icon on the track, no surface.
                      'text-muted-foreground/70 hover:text-foreground',
                      // Selected lifts off it: white pill, a real border, a shadow,
                      // and a full-strength icon. Three cues, not just a colour
                      // shift, so the choice is legible at a glance down 36 rows.
                      // `z-10` so it paints above its siblings whatever the DOM
                      // order: the source sticker hangs off this chip's corner,
                      // and a later sibling would otherwise cover it.
                      'peer-checked:z-10 peer-checked:bg-background peer-checked:text-foreground peer-checked:shadow-sm peer-checked:ring-1 peer-checked:ring-border',
                      'peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1',
                      'peer-disabled:cursor-not-allowed peer-disabled:opacity-40 peer-disabled:shadow-none peer-disabled:hover:text-muted-foreground/70',
                    )}
                  >
                    <Icon className="size-4" aria-hidden />
                    <span className="sr-only">{label}</span>
                    {/*
                      Same two icons the settings sidebar uses for the same two
                      scopes: Building2 on `Workspace > General`, UserRound on
                      `Personal > Profile` (app-sidebar-nav.ts). A member who has
                      seen that nav already knows which is which.
                    */}
                    {showSourceSticker ? (
                      <span
                        aria-hidden
                        data-testid="spec-tool-source-marker"
                        data-source={!inherited ? 'member' : fromLayerBelow ? 'shared' : 'default'}
                        className="absolute -right-1 -top-1 z-10 flex size-3.5 items-center justify-center rounded-full border bg-background"
                      >
                        <SourceIcon className="size-2 text-muted-foreground" />
                      </span>
                    ) : null}
                  </label>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{label}</p>
                  {flooredOut && explanation ? (
                    <p className="mt-1 max-w-xs text-muted-foreground">{explanation}</p>
                  ) : showSourceSticker ? (
                    <p className="mt-1 text-muted-foreground">{sourceLabel}</p>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        })}
      </fieldset>
      {/*
        Kept in the DOM whenever a floor applies, because it is what
        `aria-describedby` on every locked radio points at. Visually hidden: at
        this density a sentence under every row would be the noise this layout
        exists to remove, and the same text is on each locked label's `title`.
      */}
      {explanation ? (
        <span id={explanationId} className="sr-only">
          {explanation}
        </span>
      ) : null}
    </div>
  );
}
